import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { API } from "@/context/AuthContext";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from "recharts";
import { Users, Clock, Euro, TrendingDown, ChevronDown, ChevronUp, FileText, Loader2, Download, AlertTriangle } from "lucide-react";

const fmt = (n) => n?.toLocaleString("es-ES") ?? "0";
const fmtH = (n) => `${fmt(Math.round(n ?? 0))}h`;
const fmtE = (n) => `${fmt(Math.round(n ?? 0))}€`;

function KPI({ label, value, sub, color = "green", icon: Icon }) {
  const colors = {
    green: "bg-[#f0fdf4] text-[#007934] border-[#a7d7b0]",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red: "bg-red-50 text-red-600 border-red-200",
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`} data-testid={`kpi-${label}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium opacity-70 uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold font-['Outfit'] mt-1">{value}</p>
          {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
        </div>
        {Icon && <Icon size={18} className="opacity-40 mt-1" />}
      </div>
    </div>
  );
}

function ResourceRow({ r, projects, onRateChange }) {
  const [open, setOpen] = useState(false);
  const [editingRate, setEditingRate] = useState(false);
  const [rateInput, setRateInput] = useState(r.cost_per_hour);
  const [saving, setSaving] = useState(false);
  const completionColor = r.completion_pct >= 70 ? "bg-[#007934]" : r.completion_pct >= 40 ? "bg-amber-500" : "bg-red-500";

  // Recalculate costs live with edited rate
  const rate = parseFloat(rateInput) || r.cost_per_hour;
  const costDone = Math.round(r.hours_done * rate);
  // P01 (roadmap-valoriza-revision-despliegue-2026-10-12, E06) · si TODAS
  // las tareas abiertas de esta persona carecen de horas estimadas, el
  // coste restante no es "0€" (coste conocido cero) sino N/D — antes la
  // fila mostraba "0h"/"0€" igual que una persona sin trabajo pendiente.
  const allOpenWithoutHours = r.open_tasks > 0 && r.open_tasks_without_hours === r.open_tasks;
  const someWithoutHours = r.open_tasks_without_hours > 0 && !allOpenWithoutHours;
  const costRemaining = Math.round(r.hours_remaining * rate);

  const saveRate = async (e) => {
    e.stopPropagation();
    setSaving(true);
    try {
      await axios.post(`${API}/resources/set-rate`, { name: r.name, cost_per_hour: parseFloat(rateInput) });
      onRateChange(r.name, parseFloat(rateInput));
      setEditingRate(false);
    } catch {
      // soft fail - still update display
      onRateChange(r.name, parseFloat(rateInput));
      setEditingRate(false);
    } finally { setSaving(false); }
  };

  return (
    <>
      <tr
        className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors"
        onClick={() => setOpen(o => !o)}
        data-testid={`resource-row-${r.name.replace(/\s/g, "-")}`}
      >
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-[#007934]/10 text-[#007934] flex items-center justify-center text-xs font-bold flex-shrink-0">
              {r.name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
            </div>
            <span className="font-medium text-sm text-gray-900">{r.name}</span>
          </div>
        </td>
        <td className="py-3 px-4 text-right">
          <span className="text-sm font-semibold text-gray-900">{r.total_tasks}</span>
          <span className="text-xs text-gray-400 ml-1">({r.open_tasks} abiertas)</span>
        </td>
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${completionColor}`} style={{ width: `${r.completion_pct}%` }} />
            </div>
            <span className="text-xs text-gray-500 w-8 text-right">{r.completion_pct}%</span>
          </div>
        </td>
        <td className="py-3 px-4 text-right text-sm text-gray-700">{fmtH(r.hours_total)}</td>
        <td className="py-3 px-4 text-right text-sm text-gray-700" data-testid={`hours-remaining-${r.name.replace(/\s/g, "-")}`}>
          {allOpenWithoutHours ? <span className="text-amber-600 font-semibold" title={`${r.open_tasks} tareas abiertas sin ninguna hora estimada`}>N/D</span> : fmtH(r.hours_remaining)}
          {someWithoutHours && <span className="text-[9px] text-amber-600 block">({r.open_tasks_without_hours} sin estimar)</span>}
        </td>
        <td className="py-3 px-4 text-right text-sm font-medium text-[#007934]">{(Math.round(r.hours_done * rate)).toLocaleString("es-ES")}€</td>
        <td className="py-3 px-4 text-right text-sm font-semibold text-amber-600" data-testid={`cost-remaining-${r.name.replace(/\s/g, "-")}`}>
          {allOpenWithoutHours
            ? <span title="Todas las tareas abiertas carecen de horas estimadas — el coste restante es N/D, no 0€">N/D</span>
            : `${(Math.round(r.hours_remaining * rate)).toLocaleString("es-ES")}€`}
        </td>
        <td className="py-3 px-4 text-right" onClick={e => { e.stopPropagation(); setEditingRate(true); }}>
          {editingRate ? (
            <span className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
              <input
                type="number" value={rateInput} min={0} max={999} step={5}
                onChange={e => setRateInput(e.target.value)}
                className="w-16 text-xs border border-[#007934] rounded px-1 py-0.5 text-right focus:outline-none"
                autoFocus
              />
              <span className="text-xs text-gray-400">€/h</span>
              <button onClick={saveRate} disabled={saving} className="text-[#007934] text-xs font-bold hover:underline ml-1">
                {saving ? "…" : "✓"}
              </button>
              <button onClick={e => { e.stopPropagation(); setEditingRate(false); setRateInput(r.cost_per_hour); }} className="text-gray-400 text-xs ml-0.5">✕</button>
            </span>
          ) : (
            <span className="text-xs text-gray-400 cursor-pointer hover:text-[#007934] hover:underline" title="Clic para editar tarifa">
              {rate}€/h ✏️
            </span>
          )}
          {/* P03 (roadmap-valoriza-revision-despliegue-2026-10-12, E05/E06) ·
              `rate_source` dejó de ser "estimado"|"confirmado" desde N07
              (Ola 3) — ahora es "analista_versionado_aprobado" o
              "legacy_sin_versionar_{confirmado|estimado}". Esta condición
              seguía comparando contra el valor viejo y nunca coincidía, así
              que la tarifa por defecto 65€/h dejó de mostrarse como tal. */}
          {!editingRate && (
            <div className="text-[9px] mt-0.5" data-testid={`rate-source-${r.name.replace(/\s/g, "-")}`}>
              {r.rate_source === "analista_versionado_aprobado" ? (
                <span className="text-green-600 font-semibold" title="Tarifa versionada y aprobada por Finanzas">✓ aprobada por Finanzas</span>
              ) : r.rate_source === "legacy_sin_versionar_confirmado" ? (
                <span className="text-amber-600" title="Tarifa configurada en Recursos, pero heredada: no tiene versión ni aprobación de Finanzas">heredada (sin aprobar)</span>
              ) : (
                <span className="text-amber-600" title="No hay ninguna tarifa configurada para esta persona — se usa 65€/h por defecto, no es un dato confirmado ni aprobado">estimado 65€/h (sin ficha)</span>
              )}
            </div>
          )}
        </td>
        <td className="py-3 px-4 text-center text-gray-400">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </td>
      </tr>
      {open && (
        <tr className="bg-[#f8faf9]">
          <td colSpan={9} className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* By project */}
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-3">Tareas por proyecto</p>
                <div className="space-y-2">
                  {r.by_project.slice(0, 6).map(p => (
                    <div key={p.project} className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center mb-0.5">
                          <span className="text-xs text-gray-600 truncate">{p.project}</span>
                          <span className="text-xs font-medium text-gray-800 ml-2 flex-shrink-0">{p.tasks} tareas · {fmtH(p.hours)}</span>
                        </div>
                        <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-[#007934] rounded-full" style={{ width: `${Math.min(100, (p.tasks / r.total_tasks) * 100)}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* By month */}
              {r.by_month.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-3">Horas por mes (últimos meses)</p>
                  <ResponsiveContainer width="100%" height={100}>
                    <BarChart data={r.by_month.slice(-6)} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                      <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                      <YAxis tick={{ fontSize: 9 }} />
                      <Tooltip formatter={(v) => [`${v}h`, "Horas"]} labelStyle={{ fontSize: 10 }} />
                      <Bar dataKey="hours" fill="#007934" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            {/* Cost summary */}
            <div className="mt-3 flex gap-4 text-xs border-t border-gray-200 pt-3">
              <span className="text-gray-500">Coste ejecutado: <strong className="text-[#007934]">{fmtE(r.cost_done)}</strong></span>
              <span className="text-gray-500">Coste restante estimado: <strong className="text-amber-600">{allOpenWithoutHours ? "N/D" : fmtE(r.cost_remaining)}</strong></span>
              <span className="text-gray-500">Total estimado: <strong className="text-gray-700">{fmtE(r.cost_total)}</strong></span>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function ResourceReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiReport, setAiReport] = useState("");
  const [generatingAi, setGeneratingAi] = useState(false);
  const [filterProj, setFilterProj] = useState("all");
  const [projects, setProjects] = useState([]);
  const [rates, setRates] = useState({});  // name -> override rate

  const handleRateChange = (name, newRate) => {
    setRates(prev => ({ ...prev, [name]: newRate }));
  };

  // Apply rate overrides to resources
  const resourcesWithRates = (data?.resources ?? []).map(r => ({
    ...r,
    cost_per_hour: rates[r.name] ?? r.cost_per_hour,
    cost_done: Math.round(r.hours_done * (rates[r.name] ?? r.cost_per_hour)),
    cost_remaining: Math.round(r.hours_remaining * (rates[r.name] ?? r.cost_per_hour)),
    cost_total: Math.round(r.hours_total * (rates[r.name] ?? r.cost_per_hour)),
  }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: p }] = await Promise.all([
        axios.get(`${API}/reports/resource-executive`),
        axios.get(`${API}/projects`),
      ]);
      setData(d);
      setProjects(p);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generateAi = async () => {
    setGeneratingAi(true);
    setAiReport("");
    try {
      const { data: rep } = await axios.post(`${API}/reports/resource-executive/ai`, {
        summary: data.summary,
        top_resources: data.resources.slice(0, 10),
      });
      setAiReport(rep.report);
    } catch (e) {
      setAiReport("Error al generar el informe. Por favor, inténtelo de nuevo.");
    } finally {
      setGeneratingAi(false);
    }
  };

  const filteredResources = resourcesWithRates.filter(r => {
    if (filterProj === "all") return true;
    return r.by_project.some(p => p.project === filterProj);
  }) ?? [];

  const allProjects = [...new Set(resourcesWithRates.flatMap(r => r.by_project.map(p => p.project)))].sort();

  const chartData = resourcesWithRates.slice(0, 12).map(r => ({
    name: r.name.split(" ")[0] + " " + (r.name.split(" ")[1]?.[0] ?? "") + ".",
    tareas: r.total_tasks,
    horas: Math.round(r.hours_total),
    coste: Math.round(r.cost_total / 1000),
  })) ?? [];

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 size={32} className="animate-spin text-[#007934]" />
    </div>
  );

  const s = data?.summary ?? {};

  return (
    <div className="space-y-6 max-w-[1400px]">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">Informe Ejecutivo de Recursos</h1>
          <p className="text-sm text-gray-400 mt-1">Tareas asignadas, horas y coste por recurso, proyecto y periodo</p>
        </div>
        <button
          onClick={generateAi}
          disabled={generatingAi}
          className="flex items-center gap-2 bg-[#007934] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#006528] disabled:opacity-60 transition-colors"
          data-testid="generate-ai-resource-report"
        >
          {generatingAi ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
          Generar informe Pando
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <KPI label="Con tareas asignadas" value={s.people} sub={`${s.active_roster ?? 0} en plantilla`} icon={Users} color="green" />
        <KPI label="Tareas totales" value={fmt(s.total_tasks)} icon={FileText} color="blue" />
        <KPI label="Completadas" value={fmt(s.completed_tasks)} sub={`${s.total_tasks ? Math.round(s.completed_tasks/s.total_tasks*100) : 0}%`} color="green" icon={TrendingDown} />
        <KPI label="Abiertas" value={fmt(s.open_tasks)} sub={s.open_tasks_without_hours ? `${s.open_tasks_without_hours} sin horas` : "cobertura completa"} color="amber" icon={Clock} />
        <KPI label="Horas totales" value={fmtH(s.hours_total)} color="blue" icon={Clock} />
        <KPI label="Horas restantes" value={fmtH(s.hours_remaining)} color="amber" icon={Clock} />
        <KPI label="Coste restante" value={fmtE(s.cost_remaining)} sub="estimado (solo tareas con horas)" color="red" icon={Euro} />
      </div>

      {/* N07 (roadmap-valoriza-jev-2026-10-11, Ola 3) · "las tareas abiertas
          sin horas explican cobertura y N/D económico" — no se presentan
          como coste 0, se marcan explícitamente como N/D. */}
      {s.open_tasks_without_hours > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700 flex items-center gap-2" data-testid="resource-report-sin-horas-banner">
          <AlertTriangle size={14} />
          <span>
            <b>{fmt(s.open_tasks_without_hours)}</b> de {fmt(s.open_tasks)} tareas abiertas no tienen ninguna estimación de horas. Su coste restante es <b>N/D</b>, no 0€ — &ldquo;Coste restante&rdquo; solo refleja las {fmt(s.open_tasks_with_hours)} tareas con horas estimadas.
          </span>
        </div>
      )}

      {/* Iter313 · O04 — variantes de nombre que podrían ser la misma
          persona: no se fusionan automáticamente (riesgo de mezclar dos
          personas reales), sólo se marcan para revisión manual. */}
      {data?.possible_name_duplicates?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="name-duplicates-panel">
          <p className="text-xs font-bold text-amber-800 mb-2">⚠ Posibles variantes del mismo nombre (revisar antes de sumar capacidad)</p>
          <div className="space-y-1">
            {data.possible_name_duplicates.map((d, i) => (
              <div key={i} className="text-xs text-amber-700" data-testid={`name-duplicate-${i}`}>
                <b>{d.name_a}</b> ↔ <b>{d.name_b}</b> <span className="text-amber-500">({Math.round(d.similarity * 100)}% similar)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h2 className="text-sm font-bold text-gray-900 mb-4">Top recursos — Tareas y horas asignadas</h2>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 0, right: 20, left: 0, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#9ca3af" }} angle={-30} textAnchor="end" interval={0} />
              <YAxis yAxisId="l" tick={{ fontSize: 10, fill: "#9ca3af" }} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: "#9ca3af" }} />
              <Tooltip formatter={(v, n) => [n === "coste" ? `${v}K€` : n === "horas" ? `${v}h` : v, n]} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }} />
              <Bar yAxisId="l" dataKey="tareas" name="Tareas" fill="#007934" radius={[3,3,0,0]} />
              <Bar yAxisId="r" dataKey="horas" name="Horas" fill="#86efac" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3">
        <label className="text-xs text-gray-500 font-medium">Filtrar por proyecto:</label>
        <select
          value={filterProj}
          onChange={e => setFilterProj(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#007934]"
          data-testid="filter-project-select"
        >
          <option value="all">Todos los proyectos</option>
          {allProjects.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <span className="text-xs text-gray-400">{filteredResources.length} recursos</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full" data-testid="resource-report-table">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Recurso</th>
              <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tareas</th>
              <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Completado</th>
              <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">H. Total</th>
              <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">H. Restantes</th>
              <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Coste ejec.</th>
              <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Coste restante</th>
              <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tarifa</th>
              <th className="py-3 px-4 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {filteredResources.map(r => (
              <ResourceRow key={r.name} r={r} projects={projects} onRateChange={handleRateChange} />
            ))}
          </tbody>
          {/* Totals footer */}
          <tfoot>
            <tr className="bg-gray-50 border-t-2 border-gray-200 font-semibold">
              <td className="py-3 px-4 text-sm text-gray-700">TOTAL ({filteredResources.length} recursos)</td>
              <td className="py-3 px-4 text-right text-sm text-gray-900">{fmt(filteredResources.reduce((a,r)=>a+r.total_tasks,0))}</td>
              <td className="py-3 px-4"></td>
              <td className="py-3 px-4 text-right text-sm text-gray-900">{fmtH(filteredResources.reduce((a,r)=>a+r.hours_total,0))}</td>
              <td className="py-3 px-4 text-right text-sm text-gray-900">{fmtH(filteredResources.reduce((a,r)=>a+r.hours_remaining,0))}</td>
              <td className="py-3 px-4 text-right text-sm text-[#007934]">{fmtE(filteredResources.reduce((a,r)=>a+r.cost_done,0))}</td>
              <td className="py-3 px-4 text-right text-sm text-amber-600">{fmtE(filteredResources.reduce((a,r)=>a+r.cost_remaining,0))}</td>
              <td colSpan={2}></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* AI Report */}
      {aiReport && (
        <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="ai-resource-report">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-gray-900">Análisis ejecutivo generado por Pando</h2>
            <button
              onClick={() => {
                const blob = new Blob([aiReport], { type: "text/html" });
                const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
                a.download = "informe_recursos.html"; a.click();
              }}
              className="flex items-center gap-1 text-xs text-[#007934] hover:underline"
            >
              <Download size={12} /> Descargar
            </button>
          </div>
          <div className="prose prose-sm max-w-none text-gray-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(aiReport) }} />
        </div>
      )}
    </div>
  );
}
